/**
 * Sentence boundary helpers for TipTap voice selection commands.
 * Module-level (stable) so useCallback hooks do not need them in dependency arrays.
 */

const isSentenceEndChar = (ch) =>
  /[.!?。？！\u05C3\u061F\u066B\n]/.test(ch);

const trimSentenceRange = (text, start, end) => {
  let s = start;
  let e = end;
  while (s < e && /\s/.test(text[s])) s += 1;
  while (e > s && /\s/.test(text[e - 1])) e -= 1;
  return s < e ? { start: s, end: e } : null;
};

/** Sentence containing `offset` (inclusive of punctuation). */
export const getCurrentSentenceRange = (text, offset) => {
  if (!text) return null;
  const clamped = Math.max(0, Math.min(offset, text.length));

  let start = 0;
  for (let i = clamped - 1; i >= 0; i -= 1) {
    if (isSentenceEndChar(text[i])) {
      start = i + 1;
      break;
    }
  }

  let end = text.length;
  for (let i = clamped; i < text.length; i += 1) {
    if (isSentenceEndChar(text[i])) {
      end = i + 1;
      break;
    }
  }

  return trimSentenceRange(text, start, end);
};

/** Sentence immediately before the one containing `offset`. */
export const getPreviousSentenceRange = (text, offset) => {
  if (!text) return null;
  const clamped = Math.max(0, Math.min(offset, text.length));

  let currentStart = 0;
  for (let i = clamped - 1; i >= 0; i -= 1) {
    if (isSentenceEndChar(text[i])) {
      currentStart = i + 1;
      break;
    }
  }

  const searchEnd = currentStart - 1;
  if (searchEnd < 0) return null;

  let prevEnd = -1;
  for (let i = searchEnd; i >= 0; i -= 1) {
    if (isSentenceEndChar(text[i])) {
      prevEnd = i;
      break;
    }
  }

  if (prevEnd >= 0) {
    let prevStart = 0;
    for (let i = prevEnd - 1; i >= 0; i -= 1) {
      if (isSentenceEndChar(text[i])) {
        prevStart = i + 1;
        break;
      }
    }
    return trimSentenceRange(text, prevStart, prevEnd + 1);
  }

  return trimSentenceRange(text, 0, searchEnd + 1);
};

/** Sentence immediately after the one containing `offset`. */
export const getNextSentenceRange = (text, offset) => {
  if (!text) return null;
  const clamped = Math.max(0, Math.min(offset, text.length));

  let currentEnd = text.length;
  for (let i = clamped; i < text.length; i += 1) {
    if (isSentenceEndChar(text[i])) {
      currentEnd = i + 1;
      break;
    }
  }

  let nextStart = currentEnd;
  while (nextStart < text.length && /\s/.test(text[nextStart])) nextStart += 1;
  if (nextStart >= text.length) return null;

  let nextEnd = text.length;
  for (let i = nextStart; i < text.length; i += 1) {
    if (isSentenceEndChar(text[i])) {
      nextEnd = i + 1;
      break;
    }
  }

  return trimSentenceRange(text, nextStart, nextEnd);
};

export const getLastSentenceRange = (text) => {
  if (!text) return null;
  return (
    getPreviousSentenceRange(text, text.length) ??
    getCurrentSentenceRange(text, text.length)
  );
};

export const getFirstSentenceRange = (text) => {
  if (!text) return null;
  return getCurrentSentenceRange(text, 0);
};

export const findCursorTextBlock = ($from) => {
  for (let depth = $from.depth; depth >= 0; depth -= 1) {
    const node = $from.node(depth);
    const text = node.textContent || "";
    if (text.length > 0) {
      return {
        text,
        contentStart: $from.start(depth),
        cursorOffset: $from.pos - $from.start(depth),
      };
    }
  }
  return null;
};

export const collectEditorTextBlocks = (doc) => {
  const blocks = [];
  doc.descendants((node, pos) => {
    if (
      node.type.name === "paragraph" ||
      node.type.name === "listItem" ||
      node.type.name.startsWith("heading")
    ) {
      const text = node.textContent || "";
      if (text.trim()) {
        blocks.push({ text, contentStart: pos + 1 });
      }
    }
  });
  return blocks;
};

export const findBlockIndexAtPos = (blocks, docPos) => {
  for (let i = 0; i < blocks.length; i += 1) {
    const b = blocks[i];
    const contentEnd = b.contentStart + b.text.length;
    if (docPos >= b.contentStart && docPos <= contentEnd) return i;
  }
  return -1;
};

/** Depth of the `<p>` node containing the cursor, or null. */
export const findParagraphDepth = ($from) => {
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type.name === "paragraph") return depth;
  }
  return null;
};

const paragraphRangeFromNode = (doc, beforePos, node) => {
  const $inside = doc.resolve(Math.min(beforePos + 1, doc.content.size));
  for (let depth = $inside.depth; depth > 0; depth -= 1) {
    if ($inside.node(depth) === node) {
      return { from: $inside.start(depth), to: $inside.end(depth) };
    }
  }
  return null;
};

/** Full text range of the single `<p>` the cursor is in. */
export const getParagraphSelectionRange = ($from) => {
  const depth = findParagraphDepth($from);
  if (depth == null) return null;
  return { from: $from.start(depth), to: $from.end(depth) };
};

/**
 * All consecutive sibling `<p>` nodes around the cursor (stops at headings, tables, lists, etc.).
 */
export const getParagraphBlockRange = ($from, doc = null) => {
  const depth = findParagraphDepth($from);
  if (depth == null) return null;

  const parentDepth = depth - 1;
  const parent = $from.node(parentDepth);
  let startIdx = $from.index(parentDepth);
  let endIdx = startIdx;

  while (
    startIdx > 0 &&
    parent.child(startIdx - 1).type.name === "paragraph"
  ) {
    startIdx -= 1;
  }
  while (
    endIdx < parent.childCount - 1 &&
    parent.child(endIdx + 1).type.name === "paragraph"
  ) {
    endIdx += 1;
  }

  const document = doc || $from.doc;
  if (!document) return null;

  // $from.before(0) throws — doc's direct children use base position 0.
  const parentPos = parentDepth === 0 ? 0 : $from.before(parentDepth);
  let offset = 0;
  let from = null;
  let to = null;

  for (let i = 0; i < parent.childCount; i += 1) {
    const child = parent.child(i);
    if (i >= startIdx && i <= endIdx && child.type.name === "paragraph") {
      const range = paragraphRangeFromNode(
        document,
        parentPos + 1 + offset,
        child,
      );
      if (range) {
        if (from == null) from = range.from;
        to = range.to;
      }
    }
    offset += child.nodeSize;
  }

  if (from == null || to == null || from >= to) {
    return null;
  }
  return { from, to };
};

/** Every run of consecutive `<p>` siblings in the document (between headings/tables/etc.). */
export const listParagraphBlockRanges = (doc) => {
  const blocks = [];

  const walkParent = (parent, basePos) => {
    let offset = 0;
    let i = 0;

    while (i < parent.childCount) {
      const child = parent.child(i);

      if (child.type.name === "paragraph") {
        let from = null;
        let to = null;
        let j = i;

        while (j < parent.childCount && parent.child(j).type.name === "paragraph") {
          const node = parent.child(j);
          const range = paragraphRangeFromNode(doc, basePos + 1 + offset, node);
          if (range) {
            if (from == null) from = range.from;
            to = range.to;
          }
          offset += node.nodeSize;
          j += 1;
        }

        if (from != null && to != null && from < to) {
          blocks.push({ from, to });
        }
        i = j;
        continue;
      }

      const pos = basePos + 1 + offset;
      if (child.content.size > 0) {
        walkParent(child, pos);
      }
      offset += child.nodeSize;
      i += 1;
    }
  };

  walkParent(doc, 0);
  return blocks;
};

export const findParagraphBlockRangeIndex = (blocks, doc, docPos) => {
  for (let i = 0; i < blocks.length; i += 1) {
    const { from, to } = blocks[i];
    if (docPos >= from && docPos < to) return i;
  }

  const resolvePos = Math.min(
    Math.max(1, docPos),
    Math.max(1, doc.content.size - 1),
  );
  const current = getParagraphBlockRange(doc.resolve(resolvePos), doc);
  if (!current) return -1;

  return blocks.findIndex(
    (r) => r.from === current.from && r.to === current.to,
  );
};
